import { Button } from '@ophelos/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@ophelos/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@ophelos/ui/components/popover';
import { Building2, CheckCircle, ChevronsUpDown, CircleSlash, Layers, Loader2, Plus } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useHub } from '@/contexts/HubContext';
import { cn } from '@/lib/utils';
import { useUserPermissions } from '../../hooks/useUserPermissions';

export function ContextSwitcher({ size = 'default' }: { size?: 'sm' | 'default' }) {
  const { selectedOperator, clients, selectedClient, setSelectedClient, setOperatorWideView, isOperatorWideView } = useHub();
  const [open, setOpen] = React.useState(false);
  const [switching, setSwitching] = React.useState(false);
  const { hasPermission } = useUserPermissions();
  const navigate = useNavigate();
  const canCreateClient = hasPermission('config:admin');

  const getDisplayText = () => {
    if (selectedClient) {
      return selectedClient.display_name;
    }
    return 'All Clients';
  };

  const getDisplayIcon = () => {
    if (selectedClient) return Building2;
    return Layers;
  };

  const DisplayIcon = getDisplayIcon();
  const isOperatorView = !selectedClient;

  const handleClientSwitch = async (client: (typeof clients)[0]) => {
    if (client.id === selectedClient?.id) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      setSelectedClient(client);
      setOpen(false);
    } finally {
      setSwitching(false);
    }
  };

  const handleOperatorWideViewSwitch = async () => {
    if (isOperatorWideView) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      setOperatorWideView();
      setOpen(false);
    } finally {
      setSwitching(false);
    }
  };

  const handleCreateClient = () => {
    navigate('/settings/onboarding');
    setOpen(false);
  };

  if (clients.length === 0) {
    return (
      <Button
        variant="outline"
        size={size === 'sm' ? 'xs' : 'default'}
        disabled
        className="gap-3 px-2 text-sm font-normal border-transparent shadow-none"
      >
        <CircleSlash className="size-3.5" />
        <span className="max-w-[200px] truncate">No Client found</span>
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={size === 'sm' ? 'xs' : 'default'}
          className={cn(
            'shadow-none ml-2 gap-3 px-2 text-sm font-normal',
            isOperatorView
              ? 'border-transparent bg-transparent'
              : 'bg-accent/40 hover:bg-accent/75 border border-ring/25 hover:border-ring/50 text-accent-foreground dark:bg-accent dark:hover:bg-accent/75 dark:border-ring'
          )}
          disabled={switching}
        >
          {switching ? <Loader2 className="size-3.5 animate-spin" /> : <DisplayIcon className="size-3.5" />}
          <span className="max-w-[200px] truncate ">{getDisplayText()}</span>
          <ChevronsUpDown className={`h-3 w-3 ${isOperatorView ? 'text-accent-muted' : 'text-accent-foreground'} opacity-10`} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 rounded-xl!">
        <Command>
          <CommandInput placeholder="Search clients & debt types..." />
          <CommandList className="max-h-[50vh]">
            <CommandGroup>
              <CommandItem
                onSelect={handleOperatorWideViewSwitch}
                className={cn(
                  'rounded-lg text-sm py-3 cursor-pointer border border-transparent ',
                  isOperatorWideView && 'bg-accent/50 border-ring/15 data-[selected=true]:bg-accent/50 data-[selected=true]:border-ring/15'
                )}
                disabled={switching}
              >
                <Layers className={cn('mr-2 h-4 w-4', isOperatorWideView ? 'text-accent-foreground/50' : 'text-muted-foreground/50')} />
                <span className={cn('font-medium', isOperatorWideView ? 'text-accent-foreground' : 'text-muted-foreground')}>
                  All {selectedOperator?.name}
                </span>
                <CheckCircle className={cn('ml-auto h-4 w-4 text-accent-foreground', isOperatorWideView ? 'opacity-100' : 'opacity-0')} />
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandEmpty>No results found.</CommandEmpty>

            {/* Clients Group */}
            {clients.length > 0 && (
              <CommandGroup heading="Clients">
                {clients.map((client) => (
                  <CommandItem
                    key={client.id}
                    onSelect={() => handleClientSwitch(client)}
                    className={cn(
                      'not-first:mt-1 rounded-lg text-sm flex items-start cursor-pointer border border-transparent data-[selected=true]:bg-soft data-[selected=true]:border-border-light',
                      selectedClient?.id === client.id &&
                        'bg-accent/50 border-ring/15 data-[selected=true]:bg-accent/50 data-[selected=true]:border-ring/15'
                    )}
                    disabled={switching}
                  >
                    <Building2
                      className={`mr-2 h-4 w-4 mt-1 ${selectedClient?.id === client.id ? 'text-accent-foreground/50' : 'text-muted-foreground/50'}`}
                    />
                    <div className="flex flex-col flex-1">
                      <span className={`font-medium ${selectedClient?.id === client.id ? 'text-accent-foreground' : ''}`}>
                        {client.display_name}
                      </span>
                      <span className={`text-xs ${selectedClient?.id === client.id ? 'text-accent-foreground' : 'text-muted-foreground'}`}>
                        {client.legal_name}
                      </span>
                    </div>
                    <CheckCircle
                      className={cn(
                        'ml-2 mt-1 h-4 w-4 shrink-0 text-accent-foreground',
                        selectedClient?.id === client.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {clients.length === 0 && (
              <div className="p-1 stack">
                <span className="text-muted-foreground px-2 py-1.5 text-xs font-medium">No Results</span>
                <span className="px-2 py-1.5 text-sm bg-soft rounded">No clients found.</span>
              </div>
            )}
          </CommandList>
          <CommandSeparator className="-mx-1" />
          {canCreateClient && (
            <div className="px-2 pt-2 pb-2 bg-card -mx-1 -mb-1">
              <Button variant="outline" size="sm" className="w-full" onClick={() => handleCreateClient()}>
                <Plus />
                Create New Client
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
